import React, { useMemo } from "react";
import { Table } from "antd";
import { formatAmount } from "./money";


const sum = (rows, key) =>
  rows.reduce((total, row) => total + Number(row?.[key] || 0), 0);

export const buildGstSummary = (items = []) => {
  const bySlab = new Map();

  items.forEach((item) => {
    const rate = Number(item?.gst_percent ?? item?.gst_percentage ?? 0);
    if (!bySlab.has(rate)) {
      bySlab.set(rate, {
        gst_percent: rate,
        total: 0,
        discount: 0,
        taxable: 0,
        sgst: 0,
        cgst: 0,
        igst: 0,
        cess: 0,
        gst_total: 0,
      });
    }
    const row = bySlab.get(rate);
    const sgst = Number(item?.sgst || 0);
    const cgst = Number(item?.cgst || 0);
    const igst = Number(item?.igst || 0);
    const taxable = Number(item?.taxable_amount || 0);
    const discount = Number(item?.discount || 0);

    row.taxable += taxable;
    row.discount += discount;
    row.total += taxable + discount;
    row.sgst += sgst;
    row.cgst += cgst;
    row.igst += igst;
    /* Cess stands outside the GST total - it is a separate levy, and a total
       that swallowed it would not be the slab it is printed under. */
    row.cess += Number(item?.cess || 0);
    row.gst_total += sgst + cgst + igst;
  });

  return [...bySlab.values()].sort((a, b) => a.gst_percent - b.gst_percent);
};

const money = (value) => formatAmount(Number(value || 0));

const GstSummaryTable = ({ items = [], rows }) => {
  const data = useMemo(() => {
    const source = rows?.length ? rows : buildGstSummary(items);
    const used = source.filter(
      (row) =>
        Number(row?.total || 0) !== 0 ||
        Number(row?.taxable || 0) !== 0 ||
        Number(row?.gst_total || 0) !== 0
    );
    return used.length ? used : source.slice(0, 1);
  }, [rows, items]);

  /*
   * IGST and cess are shown only where something is actually charging them.
   *
   * A purchase within the state is split into SGST and CGST and carries no
   * IGST at all; one across state lines is the other way round. Printing both
   * sets always would put a column of zeroes beside every figure that
   * matters, and the table is narrow enough already.
   */
  const carries = (key) => data.some((row) => Number(row?.[key] || 0) !== 0);
  const moneyColumns = [
    { title: "Total", key: "total" },
    { title: "Disc", key: "discount" },
    { title: "Taxable", key: "taxable" },
    ...(carries("sgst") || !carries("igst")
      ? [
          { title: "SGST", key: "sgst" },
          { title: "CGST", key: "cgst" },
        ]
      : []),
    ...(carries("igst") ? [{ title: "IGST", key: "igst" }] : []),
    ...(carries("cess") ? [{ title: "Cess", key: "cess" }] : []),
    { title: "GST Total", key: "gst_total" },
  ];

  const columns = [
    {
      title: "GST %",
      dataIndex: "gst_percent",
      key: "gst_percent",
      render: (value) => `GST ${Number(value || 0)}%`,
    },
    ...moneyColumns.map((column) => ({
      ...column,
      dataIndex: column.key,
      align: "right",
      render: money,
    })),
  ];

  return (
    <Table
      size="small"
      pagination={false}
      rowKey="gst_percent"
      columns={columns}
      dataSource={data}
      scroll={{ x: "max-content" }}
      summary={() => (
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}>
            <strong>Total</strong>
          </Table.Summary.Cell>
          {moneyColumns.map((column, index) => (
            <Table.Summary.Cell key={column.key} index={index + 1} align="right">
              <strong>{money(sum(data, column.key))}</strong>
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      )}
    />
  );
};

export default GstSummaryTable;
